import { useAtom } from 'jotai';

import { useSelectedDealerId } from '@lotlinx-aws/dealer-selector';
import { userAtom } from 'store';
import { DateRangeKey, selectedDatePeriodAtom } from 'store/datePeriodAtom';

type QueryKey = (string | number | boolean | null | undefined)[];

interface QueryKeys {
  inventoryKey: QueryKey;
  inventoryMTDKey: QueryKey;
  inventoryPrevMonthKey: QueryKey;
  inventoryMetricsKey: QueryKey;
  inventoryHealthThresholdsKey: QueryKey;
  inventoryItemKey: QueryKey;
  vinDetailInventoryKey: QueryKey;
  dealerListKey: QueryKey;
  marketDemandDataKey: QueryKey;
  keepAliveKey: QueryKey;
  invTableConfigKey: QueryKey;
  similarVinsKey: QueryKey;
  VDPPriceDataKey: QueryKey;
  VVODataKey: QueryKey;
  VVOVinDetailDataKey: QueryKey;
  VVOSpendDataKey: QueryKey;
  VVOVinDetailSpendKey: QueryKey;
  DashboardLayoutsKey: QueryKey;
  marketSegmentsKey: QueryKey;
  leadsKey: QueryKey;
  geoConfigKey: QueryKey;
  lxVideoConfigKey: QueryKey;
  vmxAccess: QueryKey;
  permissionRequests: QueryKey;
  showroomKey: QueryKey;
  photoAiKey: QueryKey;
  campaignDefaults: QueryKey;
  campaignPromos: QueryKey;
  campaignAuthorizedList: QueryKey;
  campaignConversionReportKey: QueryKey;
  tooltips: QueryKey;
  eventsKey: QueryKey;
  inventoryBreakdownKey: QueryKey;
  acquisitionKey: QueryKey;
  carryoverChartBreakdownKey: QueryKey;
  vinPathPriceKey: QueryKey;
  cacheStatusKey: QueryKey;
  initialCacheStatusKey: QueryKey;
  inquiryConversationListKey: QueryKey;
  performanceResultsNotificationKey: QueryKey;
  quickBoostedVinsKey: QueryKey;
  campaignActivityKey: QueryKey;
  campaignsTableKey: QueryKey;
  recommendedVinBoostsKey: QueryKey;
  vsrKey: QueryKey;
  accessControlKey: QueryKey;
  campaignOverviewOverviewKey: QueryKey;
  campaignOverviewUpcomingKey: QueryKey;
  campaignOverviewInventoryKey: QueryKey;
  campaignOverviewShoppersKey: QueryKey;
  campaignOverviewDeliveryKey: QueryKey;
  campaignOverviewInvestmentKey: QueryKey;
  campaignOverviewInventoryShoppersKey: QueryKey;
  campaignListKey: QueryKey;
  piiValidateSessionKey: QueryKey;
  piiValidateAttemptsKey: QueryKey;
  lexInquiryKey: QueryKey;
  idpShoppedVinEventKey: QueryKey;
  idpProfileKey: QueryKey;
  idpRevealProfileKey: QueryKey;
  idpRecentlyRecommendedVehiclesKey: QueryKey;
  idpNotificationsKey: QueryKey;
  idpRevealTimelineKey: QueryKey;
  leadsRevivalKey: QueryKey;
}

/**
 * Plain string prefixes for a handful of the keys above, for the rare call site that
 * intentionally invalidates or looks up a query broadly — i.e. across every parameterized
 * variant at once — via TanStack's partial-key (prefix) matching, rather than one specific
 * variant. Keep each value in sync with the first element of its corresponding key above.
 */
export const QUERY_KEY_PREFIX = {
  campaignList: 'campaignList',
  campaignOverviewOverview: 'campaignOverviewOverview',
  campaignOverviewInventory: 'campaignOverviewInventory',
  campaignOverviewDelivery: 'campaignOverviewDelivery',
  campaignOverviewInvestment: 'campaignOverviewInvestment',
  leadsRevival: 'leadsRevival',
} as const;

/** Hook that provides the query key for all queries/mutations */

export default function useQueryKeys(params?: {
  vin?: string;
  enable?: boolean;
  VVOStartDate?: string;
  VVOEndDate?: string;
  vinDetailStartDate?: string;
  vinDetailEndDate?: string;
  /** Shared by the VIN detail tab queries (similar VINs, VDP price, market demand, VinPath price, leads, inventory) — distinct from the page-level `selectedDatePeriod` atom below. */
  vinDetailDateRangeKey?: DateRangeKey;
  vinDetailVvo?: boolean;
  status?: string;
  isMTD?: boolean;
  campaignId?: string | number | undefined;
  campaignPeriod?: DateRangeKey;
  piiToken?: string | null;
  piiEnabled?: boolean;
  isIDPEnabled?: boolean;
  requestId?: string;
  leadId?: string;
  /** ILM household id — identifies a Reveal shopper. */
  householdId?: number;
  eventsType?: 'login' | 'userActivity';
  eventsStartDate?: string;
  eventsEndDate?: string;
  /** yyyyMMdd range for the identified-shopper (Reveal) queries. */
  idpStartDate?: string;
  idpEndDate?: string;
}): QueryKeys {
  const [user] = useAtom(userAtom);
  const [dealerId] = useSelectedDealerId();
  const [selectedDatePeriod] = useAtom(selectedDatePeriodAtom);

  const token = user?.token ?? '';

  return {
    inventoryKey: ['inventory', dealerId, selectedDatePeriod],
    inventoryMTDKey: ['inventory', dealerId],
    inventoryPrevMonthKey: ['inventory', dealerId],
    // No date or threshold discriminators: the endpoint always answers "right now" and the six
    // threshold sliders are applied client-side against this one response — see
    // `inventoryHealthThresholdsKey` for the persisted thresholds themselves.
    inventoryMetricsKey: ['inventoryMetrics', dealerId],
    // Scoped per-user as well as per-dealer, matching `geoConfigKey`/`showroomKey` above, even
    // though the endpoint itself is per-dealer only (see `useInventoryHealthThresholds`).
    inventoryHealthThresholdsKey: ['inventoryHealthThresholds', dealerId, user?.id],
    inventoryItemKey: ['inventoryItem', dealerId, params?.vin],
    vinDetailInventoryKey: [
      'inventoryVinDetail',
      dealerId,
      params?.vinDetailDateRangeKey,
      params?.vinDetailStartDate,
      params?.vinDetailEndDate,
      params?.vinDetailVvo,
    ],
    dealerListKey: ['dealerList', user?.id ?? ''],
    marketDemandDataKey: [
      'marketDemandData',
      dealerId,
      token,
      params?.vin,
      params?.vinDetailDateRangeKey,
      params?.vinDetailStartDate,
      params?.vinDetailEndDate,
    ],
    keepAliveKey: ['keepalive', token],
    invTableConfigKey: ['inventoryTableConfig', user?.id],
    similarVinsKey: [
      'priceScatterplotData',
      dealerId,
      token,
      params?.vin,
      params?.vinDetailDateRangeKey,
      params?.vinDetailStartDate,
      params?.vinDetailEndDate,
    ],
    VDPPriceDataKey: [
      'vdpPriceData',
      dealerId,
      token,
      params?.vin,
      params?.vinDetailDateRangeKey,
      params?.vinDetailStartDate,
      params?.vinDetailEndDate,
    ],
    VVODataKey: ['vvoData', dealerId, params?.VVOStartDate, params?.VVOEndDate, params?.status],
    VVOVinDetailDataKey: [
      'vvoVinDetailData',
      dealerId,
      params?.VVOStartDate,
      params?.VVOEndDate,
      params?.status,
    ],
    VVOSpendDataKey: ['vvoSpendData', dealerId, params?.VVOStartDate, params?.VVOEndDate],
    VVOVinDetailSpendKey: [
      'vvoVinDetailSpendData',
      dealerId,
      params?.VVOStartDate,
      params?.VVOEndDate,
    ],
    DashboardLayoutsKey: ['dashboardLayouts', dealerId, user?.id],
    marketSegmentsKey: ['marketSegments', dealerId, user?.id],
    leadsKey: [
      'leads',
      dealerId,
      token,
      params?.vin,
      params?.vinDetailDateRangeKey,
      params?.vinDetailStartDate,
      params?.vinDetailEndDate,
    ],
    geoConfigKey: ['geoConfig', dealerId, user?.id],
    lxVideoConfigKey: ['lxVideoConfig', dealerId, user?.id],
    vmxAccess: ['vmxAccess', dealerId, user?.id],
    permissionRequests: ['permissionRequests', dealerId, user?.id],
    showroomKey: ['showroomConfig', dealerId, user?.id],
    photoAiKey: ['photoAi', dealerId, user?.id],
    campaignDefaults: ['campaignDefaults', dealerId, user?.id],
    campaignPromos: ['campaignPromos', user?.id],
    campaignAuthorizedList: ['campaignAuthorizedList', dealerId, user?.id],
    campaignConversionReportKey: ['campaignConversionReport', dealerId],
    tooltips: ['tooltips', token],
    eventsKey: [
      'events',
      dealerId,
      params?.eventsType,
      params?.eventsStartDate,
      params?.eventsEndDate,
    ],
    inventoryBreakdownKey: ['inventoryBreakdown', dealerId],
    acquisitionKey: ['acquisition', dealerId],
    carryoverChartBreakdownKey: ['carryoverChartBreakdown', dealerId],
    vinPathPriceKey: [
      'vinPathPrice',
      dealerId,
      token,
      params?.vin,
      params?.vinDetailDateRangeKey,
      params?.vinDetailStartDate,
      params?.vinDetailEndDate,
    ],
    // No dealerId baked in: every consumer needs a key scoped to a *specific* dealer row (in a
    // multi-dealer list, or the currently selected one), which this hook can't provide on its
    // own since it's only callable once per component. Callers append their own dealerId via
    // spread — see useGetCacheStatus and useInitiateCacheRebuild.
    cacheStatusKey: ['cacheStatus'],
    initialCacheStatusKey: ['initialCacheStatus', dealerId],
    inquiryConversationListKey: ['inquiryConversationList', dealerId, user?.id],
    performanceResultsNotificationKey: ['performanceResultsNotification', dealerId, user?.id],
    quickBoostedVinsKey: ['quickBoostedVins', user?.token, dealerId],
    campaignActivityKey: ['campaignActivity', user?.token, dealerId],
    campaignsTableKey: ['campaignsTable', user?.token, dealerId],
    recommendedVinBoostsKey: ['recommendedVinBoosts', user?.token, dealerId],
    vsrKey: ['vsr', dealerId, user?.token],
    accessControlKey: ['accessControl', user?.id ?? '', dealerId],
    campaignOverviewOverviewKey: [
      'campaignOverviewOverview',
      dealerId,
      user?.id,
      params?.campaignId,
      params?.campaignPeriod,
      params?.isIDPEnabled,
    ],
    campaignOverviewUpcomingKey: [
      'campaignOverviewUpcoming',
      dealerId,
      user?.id,
      params?.campaignId,
    ],
    campaignOverviewInventoryKey: [
      'campaignOverviewInventory',
      dealerId,
      user?.id,
      params?.campaignId,
      params?.campaignPeriod,
      params?.piiToken,
      params?.piiEnabled,
    ],
    // One campaign, or every campaign for the range when campaignId is undefined (VMS1-6629).
    campaignOverviewShoppersKey: [
      'campaignOverviewShoppers',
      dealerId,
      user?.id,
      params?.idpStartDate,
      params?.idpEndDate,
      params?.campaignId,
      params?.piiToken,
      params?.piiEnabled,
    ],
    campaignOverviewDeliveryKey: [
      'campaignOverviewDelivery',
      dealerId,
      user?.id,
      params?.campaignId,
      params?.campaignPeriod,
    ],
    campaignOverviewInvestmentKey: [
      'campaignOverviewInvestment',
      dealerId,
      user?.id,
      params?.campaignId,
      params?.campaignPeriod,
    ],
    campaignOverviewInventoryShoppersKey: [
      'campaignOverviewInventoryShoppers',
      dealerId,
      user?.id,
      params?.vin,
      params?.campaignId,
      params?.campaignPeriod,
      params?.piiToken,
      params?.piiEnabled,
    ],
    campaignListKey: ['campaignList', dealerId, user?.id, params?.campaignPeriod],
    piiValidateSessionKey: ['piiValidateSession', user?.id],
    piiValidateAttemptsKey: ['validatePIIAttempts', token, dealerId],
    lexInquiryKey: ['lexInquiry', params?.requestId],
    idpShoppedVinEventKey: ['idpShoppedVinEvent', dealerId, params?.vin, params?.leadId],
    idpProfileKey: ['idpProfile', dealerId, params?.leadId, params?.piiToken, params?.piiEnabled],
    idpRevealProfileKey: [
      'idpRevealProfile',
      dealerId,
      params?.householdId,
      params?.piiToken,
      params?.piiEnabled,
    ],
    idpRecentlyRecommendedVehiclesKey: ['recentlyRecommendedVehicles', dealerId, params?.leadId],
    idpNotificationsKey: ['idpNotifications', dealerId, user?.id],
    idpRevealTimelineKey: ['idpRevealTimeline', dealerId, params?.idpStartDate, params?.idpEndDate],
    leadsRevivalKey: ['leadsRevival', dealerId, params?.piiToken, params?.piiEnabled],
  };
}
